import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, TemplateRef, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api.service';
import { AppSettings } from 'src/app/services/AppSettings';
import { GigsumoConstants } from 'src/app/services/GigsumoConstants';
import { JobService } from 'src/app/services/job.service';
import { UtilService } from 'src/app/services/util.service';
import { ContractCandidateService } from 'src/app/services/contract-candidate.service';
import { SubContractService, SubmittedForPayload } from 'src/app/services/sub-contract.service';
import { SearchData } from 'src/app/services/searchData';
import { PricePlanService } from '../../Price-subscritions/priceplanService';
import { CustomValidator } from '../../Helper/custom-validator';
import { environment } from 'src/environments/environment';
import { CommonSkeletonComponent } from 'src/app/skeleton/common-skeleton/common-skeleton.component';
import { MessageWidgetComponent } from 'src/app/widget/message-widget/message-widget.component';
import { MatchPercentageDetailsComponent } from '../../match-percentage-details/match-percentage-details.component';
import { TimelineComponent } from 'src/app/templates/timeline/timeline.component';
import { TimezoneDatePipe } from 'src/app/timezone-date.pipe';
import { SlidePanelService } from 'src/app/services/slide-panel.service';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { APP_ICONS } from 'src/app/icons/icon-provider';
import Swal from 'sweetalert2';
import { CompactCountPipe } from '../../../pipe/compact-count.pipe';


@Component({
  selector: 'app-candidate-card-table-view',
  standalone: true,
  imports: [
    CompactCountPipe,
    CommonModule, ReactiveFormsModule, BsDropdownModule, CommonSkeletonComponent, MessageWidgetComponent, MatchPercentageDetailsComponent, TimelineComponent,
NgIconComponent, TimezoneDatePipe,
  ],
  providers: [provideIcons(APP_ICONS)],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './candidate-card-table-view.component.html',
  styleUrls: ['./candidate-card-table-view.component.scss']
})
export class CandidateCardTableViewComponent implements OnInit, OnDestroy, OnChanges {

  // Subscription lifetime — cleanup hook for every `.subscribe(...)` in
  // this component. Parent tabs re-mount this component on every tab
  // swap, so without this pipe each tab-hop leaks observables. Emit +
  // complete in `ngOnDestroy()` below.
  private destroy$ = new Subject<void>();

  // Inputs from parent component
  @Input() pageName: any;
  @Input() response: any[] = [];
  @Input() loadAPIcall: boolean = false;
  @Input() stopscrollFlag: boolean = false;
  @Input() responseReceived: boolean = false;
  @Input() jobDetails: any = {};
  @Input() userId: string = '';
  @Input() showTimeline: boolean = false;
  @Input() From: "SUGGESTED_CANDIDATES" | "FEATURED_CANDIDATE" | "CANDIDATES_FROM_CONNECTION";
  @Input() designVersion: string = 'v2';

  @Input() filterApplicantData: any;

  // Environment URL for photos
  url = AppSettings.photoUrl;

  userType: any;

  // CEIPAL Free plan detection — hide match scores and show popup for AI features
  isCeipalFreePlan: boolean = false;

  // Get appropriate message based on page context
  get noResultMessage(): string {
    if (this.pageName === 'suggested-candidates') {
      if (!this.responseReceived) {
        return 'Our AI is analyzing this job and finding the best matching candidates for you...';
      } else {
        return 'No Marketplace Candidates Yet. Our AI is processing this job posting to find perfect matches. Please check back soon!';
      }
    } else if (this.pageName === 'talent-pool') {
      if (!this.responseReceived) {
        return 'Our AI is building your talent pool by analyzing candidates from your network, previous interactions, and job applicants...';
      } else {
        return 'No candidates in your talent pool yet. Our AI is processing this job and analyzing your network connections, previous interactions, and applicants to find the best matches. Please check back soon!';
      }
    } else {
      if (!this.responseReceived) {
        return 'Loading candidates...';
      } else {
        return 'No candidates found';
      }
    }
  }

  get noResultSvgType(): string {
    if (this.pageName === 'suggested-candidates' || this.pageName === 'talent-pool') {
      // Always show AI processing icon for AI-powered features
      return 'customAiProcessing';
    } else {
      if (this.responseReceived) {
        return 'customNotFound';
      } else {
        return 'tablerUserSearch';
      }
    }
  }

  // Chat-related properties (copied from candidate-card)
  seletedChatItem: any;
  messageData: any;
  messagemodelflag: any = false;
  connectionStatus: string;
  values: any;
  isClicked: boolean = false;

  // ── RTR / B2B payrate flow (port of candidate-card row-design)
  // The Stage dropdown can land on INTERVIEW-SCHEDULED through ONBOARDED;
  // for those stages the V2 row-design opens a `Rate Confirmation` modal
  // (RTR — Right To Represent) to collect targetRate + payType, validate
  // the B2B link via `contractCandidateService.validateB2bConnection`,
  // and create the contract candidate before persisting the status. The
  // V3 path was missing this wrapper entirely. ──
  rtrformgrp: UntypedFormGroup;
  rtrModalRef: BsModalRef;
  submitRTR: boolean = false;
  isPayrateReadonly: boolean = false;
  pendingOfferedData: any = null;
  pendingOfferedCandidate: any = null;
  b2bData: any = null;

  // Spec convenience handles — candidate-card row-design references
  // `this.jobId`, `this.jobTitle`, `this.postedByUserName`, and
  // `this.appliedJobDetails` directly (derived from `appliedJobDetails`
  // in candidate-card.ngOnInit). In this list view they all live on
  // the per-listing `jobDetails` Input, so expose them as getters with
  // the same names — that way the spec methods below can paste in
  // verbatim without textual edits.
  get jobId(): string | undefined { return this.jobDetails?.jobId; }
  get jobTitle(): string | undefined { return this.jobDetails?.jobTitle; }
  get postedByUserName(): string | undefined { return this.jobDetails?.postedByUserName; }
  get appliedJobDetails(): any { return this.jobDetails; }

  constructor(
    private JobServicecolor: JobService,
    private util: UtilService,
    private apiService: ApiService,
    private router: Router,
    private planService: PricePlanService,
    private slidePanelService: SlidePanelService,
    private formBuilder: UntypedFormBuilder,
    private modalService: BsModalService,
    private contractCandidateService: ContractCandidateService,
    private subContractService: SubContractService,
    private searchData: SearchData
  ){

  }

  ngOnInit(): void {
    this.userType = localStorage.getItem('userType');
    this.checkCeipalFreePlan();
    if (Array.isArray(this.response)) {
      this.response.forEach(r => r.showAllSkills = false);
    }
    this.sortResumeRequestsIfNeeded();
    // Locked 2026-06-08 — two-row payrate model (parity with candidate-card.ngOnInit).
    this.rtrformgrp = this.formBuilder.group({
      submittedToRate: [{ value: null, disabled: true }],
      submittedRateType: [{ value: null, disabled: true }],
      offerRate: [null, Validators.compose([Validators.required, CustomValidator.validDecimal(), CustomValidator.checkWhiteSpace()])],
      offerRateType: [null, Validators.required],
      termsandcondition: [null, Validators.requiredTrue],
      country: [null]
    });
    this.refreshTimelinesForResponse();
    this.loadBusinessNameCache();
  }

  /**
   * One-time fetch of (businessId → businessName) for connected businesses so the
   * Submit For modal can render real names instead of UUIDs. Silent failure — the
   * modal degrades to showing businessIds if the lookup errors out.
   * Skip for Draft jobs as they don't need business connections.
   */
  private loadBusinessNameCache(): void {
    // Prevent API call for Draft status jobs
    const jobStatus = this.jobDetails?.status?.toUpperCase();
    if (jobStatus === 'DRAFT' || jobStatus === 'DRAFTED') {
      console.log('[candidate-card-table-view] Skipping loadBusinessNameCache for Draft job:', this.jobDetails?.jobId, 'Status:', this.jobDetails?.status);
      return;
    }
    const myBiz = this.getViewerBusinessId();
    if (!myBiz) return;
    this.contractCandidateService.findConnections(myBiz, '')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const all: any[] = [
            ...(res?.data?.customers || []),
            ...(res?.data?.suppliers || []),
            ...(res?.data?.connections || [])
          ];
          for (const conn of all) {
            const id = conn?.businessId || conn?.id;
            const name = conn?.businessName || conn?.name;
            if (id && name) this.businessNameMap.set(String(id).toLowerCase(), name);
          }
        },
        error: () => { /* silent — UI degrades to UUIDs */ }
      });
  }

  /**
   * Prevent API calls for suggested-candidates page when job status is Draft
   */
  private preventApiCallsForDraftJobs(): boolean {
    if (this.pageName === 'suggested-candidates' || this.pageName === 'talent-pool') {
      const jobStatus = this.jobDetails?.status?.toUpperCase();
      console.log('[candidate-card-table-view] pageName:', this.pageName, 'jobStatus:', jobStatus, 'jobDetails.status:', this.jobDetails?.status);
      
      if (jobStatus === 'DRAFT' || jobStatus === 'DRAFTED') {
        console.log('[candidate-card-table-view] Blocking API calls for Draft job on suggested-candidates/talent-pool page');
        return true;
      }
    }
    return false;
  }

  // The Activity Map pipe in the applicants table reads `timelineStages`
  // off each candidate; without that the template falls back to the
  // dateless 6-step generic pipe. Loading mirrors candidate-card row-design's
  // `prepareTimeline` (jobs/findTimeLines + group-by-stage with implied
  // priors), but we run it once per candidate row and stash the result on
  // the candidate object so the existing `jc-v3-pipe-v2` template (which
  // already renders `stage.date`) takes over.
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['response'] || changes['jobDetails']) {
      this.sortResumeRequestsIfNeeded();

      // Prevent API calls for Draft jobs on suggested-candidates/talent-pool pages
      if (this.preventApiCallsForDraftJobs()) {
        return;
      }

      this.refreshTimelinesForResponse();
      this.checkCeipalFreePlan();
    }
  }

  private checkCeipalFreePlan(): void {
    const membershipType = localStorage.getItem('MemberShipType');
    this.isCeipalFreePlan = this.jobDetails?.source === 'CEIPAL' && membershipType === 'Free';
  }

  showCeipalFreePopup(): void {
    Swal.fire({
      icon: 'info',
      title: '',
      text: 'AI features are not available for CEIPAL Free plan. Please upgrade your plan to access AI-powered recommendations.',
      showConfirmButton: true,
      confirmButtonText: 'OK'
    });
  }

  get rtrFormControls() {
    return this.rtrformgrp.controls;
  }

  /** Restrict numeric input on the RTR target-rate field. Mirrors
   *  candidate-card's `isInteger`. */
  isInteger(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
      return false;
    }
    return true;
  }

  // Cleanup all tracked subscriptions — queryBlob (resume download),
  // checkConnectionStatus (chat gate), updateCandidateLiked (like toggle).
  // Without this, every tab swap that re-mounts this component leaked
  // one observable per in-flight call.
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Activity Map / Stage timeline ────────────────────────────────────
  // Mirrors the stage groupings in candidate-card row-design's
  // `prepareTimeline`. The fallback `genericPipe` shows no dates, so we
  // populate `candidate.timelineStages` and let `jc-v3-pipe-v2` render
  // each step's date (Tabler-style "12 Jan").
  private readonly TIMELINE_NEGATIVE_STATUSES = [
    'REJECTED', 'PROFILE-REJECTED', 'INTERVIEW-REJECTED', 'INTERVIEW-FAILED',
    'OFFER-DECLINED', 'CANDIDATE-WITHDRAWN', 'OFFER-WITHDRAWN'
  ];
  private readonly TIMELINE_NEGATIVE_LABELS: Record<string, string> = {
    'REJECTED': 'Rejected',
    'PROFILE-REJECTED': 'Profile Rejected',
    'INTERVIEW-REJECTED': 'Interview Rejected',
    'INTERVIEW-FAILED': 'Interview Failed',
    'OFFER-DECLINED': 'Offer Declined',
    'CANDIDATE-WITHDRAWN': 'Candidate Withdrawn',
    'OFFER-WITHDRAWN': 'Offer Withdrawn'
  };

  private refreshTimelinesForResponse(): void {
    // The stage pipe renders on both the applicants tab (job owner side)
    // and the candidate-applied tab (non-owner side, opened from /newjobs
    // when the viewer isn't the job owner). Skip the API roundtrip on
    // other tabs (talent-pool, suggested-candidates, candidates-invited,
    // etc.) where the pipe is hidden by the template gate.
    if (this.pageName !== 'job-applicants' && this.pageName !== 'candidate-applied') return;
    if (!Array.isArray(this.response) || !this.jobDetails?.jobId) return;
    this.response.forEach((candidate: any) => {
      if (!candidate || candidate._timelineRequested) return;
      candidate._timelineRequested = true;
      // V3 → verbatim port of candidate-card row-design `prepareTimeline`
      // (see candidate-card.component.ts:3305). V2 keeps existing
      // buildTimelineStages flow used by the designVersion!=='v3' markup.
      if (this.designVersion === 'v3') {
        this.prepareTimelineForCandidateV3(candidate);
      } else {
        this.loadTimelineForCandidate(candidate);
      }
    });
  }

  // ── V3 timeline (verbatim port from candidate-card row-design) ──────
  // Mirrors prepareTimeline / getDefaultTimelineStagesEnhanced /
  // negativeStages / timelineStatusLabelMap from
  // candidate-card.component.ts:3289-3381 so the V3 stage-pipe in this
  // table-view renders identically to row-design. We can iterate on
  // colors later by tweaking the .row-design-timeline-* SCSS only.
  negativeStages = ['REJECTED','PROFILE-REJECTED','INTERVIEW-REJECTED','INTERVIEW-FAILED','OFFER-DECLINED','CANDIDATE-WITHDRAWN','OFFER-WITHDRAWN'];
  timelineStatusLabelMap: Record<string, string> = {
    'REJECTED': 'Rejected',
    'PROFILE-REJECTED': 'Profile Rejected',
    'INTERVIEW-REJECTED': 'Interview Rejected',
    'INTERVIEW-FAILED': 'Interview Failed',
    'OFFER-DECLINED': 'Offer Declined',
    'CANDIDATE-WITHDRAWN': 'Candidate Withdrawn',
    'OFFER-WITHDRAWN': 'Offer Withdrawn'
  };
  // Returns the % width for the purple progress overlay on the V3 timeline
  // line. Walks back to the latest completed stage (positive OR negative),
  // so the line always reaches the dot the candidate is presently at — the
  // negative dot/label/date stay red via the `.negative` class on the step.
  // With 6 stages distributed via flex:1, dot N sits at (2N+1)/12 of the
  // track width; span from dot 0 to dot N is N/stages.length.
  getProgressLinePct(stages: any[]): number {
    if (!stages?.length) return 0;
    let idx = 0;
    for (let i = stages.length - 1; i >= 0; i--) {
      if (stages[i].completed) { idx = i; break; }
    }
    return (idx / stages.length) * 100;
  }

  getDefaultTimelineStagesEnhanced() {
    return [
      { index: 0, id: 'APPLIED',     label: 'Applied',     date: null, completed: false, negative: false, future: true, isImplied: false, relatedStatuses: ['JOB-APPLIED'] },
      { index: 1, id: 'SHORTLISTED', label: 'Shortlisted', date: null, completed: false, negative: false, future: true, isImplied: false, relatedStatuses: ['CANDIDATE-SHORTLISTED'] },
      { index: 2, id: 'INTERVIEW',   label: 'Interview',   date: null, completed: false, negative: false, future: true, isImplied: false, relatedStatuses: ['INTERVIEW-SCHEDULED', 'INTERVIEW-ACCEPTED', 'L1-INTERVIEW', 'L2-INTERVIEW', 'L3-INTERVIEW'] },
      { index: 3, id: 'SELECTED',    label: 'Selected',    date: null, completed: false, negative: false, future: true, isImplied: false, relatedStatuses: ['CANDIDATE-SELECTED'] },
      { index: 4, id: 'OFFERED',     label: 'Offered',     date: null, completed: false, negative: false, future: true, isImplied: false, relatedStatuses: ['OFFERED', 'OFFER-ACCEPTED'] },
      { index: 5, id: 'ONBOARDED',   label: 'Onboarded',   date: null, completed: false, negative: false, future: true, isImplied: false, relatedStatuses: ['ONBOARDED'] }
    ];
  }

  prepareTimelineForCandidateV3(candidate: any): void {
    if (!candidate?.candidateId || !this.jobDetails?.jobId) return;
    // Seed with the default (all-future) timeline so the V3 timeline
    // renders immediately while the API is in flight; the API success
    // path replaces this with the real grouped timeline.
    candidate.timelineDataGrouped = this.getDefaultTimelineStagesEnhanced();
    const payload = {
      entityId: this.jobDetails.jobId,
      entityType: 'JOB',
      limit: 20,
      searchAfterKey: null,
      activityOn: candidate.candidateId,
      parentActivity: 'JOB_APPLICATION,CANDIDATE_INVITATION',
      owner: this.jobDetails.jobPostedBy === localStorage.getItem('userId')
    };
    this.apiService.create('jobs/findTimeLines', payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: any) => {
          if (!res || res.code !== '00000' || !res.data) return;
          let timelines = res.data?.timeLines || [];
          if (timelines.length === 0) return;
          timelines = timelines.reverse();
          const defaultTimeline = this.getDefaultTimelineStagesEnhanced();
          let lastCompletedIndex = 0;
          for (let i = 0; i < timelines.length; i++) {
            const t = timelines[i];
            if (this.negativeStages.includes(t.activityType)) {
              const targetIndex = Math.min(lastCompletedIndex + 1, defaultTimeline.length - 1);
              const stag: any = defaultTimeline[targetIndex];
              stag.negative = true;
              stag.future = false;
              stag.completed = true;
              stag.label = this.timelineStatusLabelMap[t.activityType];
              stag.date = t.createdOn;
              break;
            }
            const stage: any = defaultTimeline.find((d: any) => d.relatedStatuses.includes(t.activityType));
            if (!stage) continue;
            stage.completed = true;
            stage.future = false;
            stage.date = t.createdOn;
            stage.negative = false;
            lastCompletedIndex = stage.index;
            for (let j = 0; j < stage.index; j++) {
              const isNotCompletedStage: any = defaultTimeline[j];
              if (isNotCompletedStage && !isNotCompletedStage.completed) {
                isNotCompletedStage.completed = true;
                isNotCompletedStage.future = false;
                isNotCompletedStage.date = t.createdOn;
                isNotCompletedStage.negative = false;
                isNotCompletedStage.isImplied = true;
              }
            }
          }
          candidate.timelineDataGrouped = defaultTimeline;
        },
        (err: any) => {
          console.error('Timeline API error for candidate:', candidate.candidateId, err);
        }
      );
  }

  private loadTimelineForCandidate(candidate: any): void {
    if (!candidate?.candidateId || !this.jobDetails?.jobId) return;
    const payload = {
      entityId: this.jobDetails.jobId,
      entityType: 'JOB',
      limit: 20,
      searchAfterKey: null,
      activityOn: candidate.candidateId,
      parentActivity: 'JOB_APPLICATION,CANDIDATE_INVITATION',
      owner: this.jobDetails.jobPostedBy === localStorage.getItem('userId')
    };
    this.apiService.create('jobs/findTimeLines', payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: any) => {
          const events = res?.data?.timeLines || [];
          // Empty events → leave `timelineStages` unset so the template's
          // `else genericPipe` branch (which still highlights the current
          // stage from `candidate.currentStatus`) takes over instead of
          // an all-grey, dateless 6-step row.
          if (events.length === 0) return;
          candidate.timelineStages = this.buildTimelineStages(events);
        },
        (err: any) => {
          console.error('Timeline API error for candidate:', candidate.candidateId, err);
        }
      );
  }

  // Convert raw `timeLines` events into the per-stage shape the
  // `jc-v3-pipe-v2` template consumes (stage.completed / .active /
  // .isNegative / .isTerminal / .isImplied / .notion / .date).
  private buildTimelineStages(events: any[]): any[] {
    const stages: any[] = [
      { index: 0, id: 'APPLIED',     label: 'Applied',     date: null, completed: false, active: false, isNegative: false, isTerminal: false, isImplied: false, notion: 'future', activityType: '', relatedStatuses: ['JOB-APPLIED'] },
      { index: 1, id: 'SHORTLISTED', label: 'Shortlisted', date: null, completed: false, active: false, isNegative: false, isTerminal: false, isImplied: false, notion: 'future', activityType: '', relatedStatuses: ['CANDIDATE-SHORTLISTED'] },
      { index: 2, id: 'INTERVIEW',   label: 'Interview',   date: null, completed: false, active: false, isNegative: false, isTerminal: false, isImplied: false, notion: 'future', activityType: '', relatedStatuses: ['INTERVIEW-SCHEDULED', 'INTERVIEW-ACCEPTED', 'L1-INTERVIEW', 'L2-INTERVIEW', 'L3-INTERVIEW'] },
      { index: 3, id: 'SELECTED',    label: 'Selected',    date: null, completed: false, active: false, isNegative: false, isTerminal: false, isImplied: false, notion: 'future', activityType: '', relatedStatuses: ['CANDIDATE-SELECTED'] },
      { index: 4, id: 'OFFERED',     label: 'Offered',     date: null, completed: false, active: false, isNegative: false, isTerminal: false, isImplied: false, notion: 'future', activityType: '', relatedStatuses: ['OFFERED', 'OFFER-ACCEPTED'] },
      { index: 5, id: 'ONBOARDED',   label: 'Onboarded',   date: null, completed: false, active: false, isNegative: false, isTerminal: false, isImplied: false, notion: 'future', activityType: '', relatedStatuses: ['ONBOARDED'] }
    ];
    if (!events?.length) return stages;
    // API returns newest first; replay oldest → newest so implied-prior
    // logic walks forward with a monotonically growing `lastCompletedIdx`.
    const ordered = [...events].reverse();
    let lastCompletedIdx = -1;
    for (const t of ordered) {
      if (this.TIMELINE_NEGATIVE_STATUSES.includes(t.activityType)) {
        const targetIdx = Math.min(lastCompletedIdx + 1, stages.length - 1);
        const stg = stages[targetIdx];
        stg.isNegative = true;
        stg.isTerminal = true;
        stg.completed = true;
        stg.active = true;
        stg.notion = 'negative';
        stg.label = this.TIMELINE_NEGATIVE_LABELS[t.activityType] || stg.label;
        stg.date = t.createdOn;
        stg.activityType = t.activityType;
        // Once a rejection/withdrawal lands, the journey stops there.
        return stages;
      }
      const stage = stages.find(s => s.relatedStatuses.includes(t.activityType));
      if (!stage) continue;
      stage.completed = true;
      stage.notion = 'positive';
      stage.date = t.createdOn;
      stage.activityType = t.activityType;
      lastCompletedIdx = stage.index;
      // Mark every prior stage as implied-completed with the same date —
      // matches candidate-card row-design behavior so the connectors
      // light up purple end-to-end.
      for (let j = 0; j < stage.index; j++) {
        const prior = stages[j];
        if (!prior.completed) {
          prior.completed = true;
          prior.isImplied = true;
          prior.notion = 'positive';
          prior.date = t.createdOn;
        }
      }
    }
    // The most-recent positive stage is the candidate's "current" — the
    // template's `[class.current]` adds the box-shadow ring around it.
    const current = [...stages].reverse().find(s => s.completed && !s.isTerminal && !s.isNegative);
    if (current) current.active = true;
    return stages;
  }

  private userTypeMap: { [key: string]: string } = {
    'FREELANCE_RECRUITER': 'Freelance Recruiter',
    'RECRUITER': 'Recruiter',
    'JOB_SEEKER': 'Job Seeker',
    'MANAGEMENT_TALENT_ACQUISITION': 'Manager',
    'MANAGEMENT TALENT ACQUISITION': 'Manager',
    'BENCH_RECRUITER': 'Bench Recruiter'
  };

  formatUserType(userType: string): string {
    if (!userType) return '';
    return this.userTypeMap[userType] || userType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  // Helper methods for UI
  // getInitials(firstName: string, lastName: string): string {
  //   const first = firstName ? firstName.charAt(0).toUpperCase() : '';
  //   const last = lastName ? lastName.charAt(0).toUpperCase() : '';
  //   return first + last;
  // }

  // getColor(firstName: string, lastName: string): string {
  //   const colors = [
  //     '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
  //     '#DFE6E9', '#74B9FF', '#A29BFE', '#FD79A8', '#FDCB6E'
  //   ];
  //   const name = (firstName || '') + (lastName || '');
  //   const index = name.length % colors.length;
  //   return colors[index];
  // }

  getInitials(fullname: string): string {
    return this.JobServicecolor.getInitials(fullname);
  }

  getColor(fullname: string): string {
    return this.JobServicecolor.getColor(fullname);
  }
  getFullNameTextColor(fullname: string): string {
    return this.JobServicecolor.getFullnameTextColor(fullname);
  }

  // Normalizes the photo URL — passes through full http(s) URLs, otherwise
  // prefixes the base photo URL (same behaviour as the V2 cards).
  getPhotoUrl(photo: string): string {
    if (!photo) return '';
    if (photo.startsWith('http://') || photo.startsWith('https://')) return photo;
    return this.url + photo;
  }

  // Toggle skills visibility
  toggleSkills(item: any): void {
    item.showAllSkills = !item.showAllSkills;
  }

  // Concatenate primary + secondary skills — drives the V3 card's
  // "Show more / Show less" expand on long skill lists. Mirrors
  // job-card.component.ts:getAllSkills(JobData) so both card variants
  // reuse the same merge order (primaries first, secondaries after).
  getAllCandidateSkills(item: any): string[] {
    const primary = item?.primarySkills || [];
    const secondary = item?.secondarySkills || [];
    return [...primary, ...secondary];
  }

  // Mirrors `APPLICANT_STATUS_COLOR` in FormValidation.ts (used by
  // candidate-card.component via inherited getStyle()), so the table-view
  // status pill matches the V2 card pill exactly.
  getStageStyle(status: string): { background: string; color: string } {
    if (!status) status = 'APPLIED';
    const normalized = status.toUpperCase();

    const statusColorMap: Record<string, { background: string; color: string }> = {
      'APPLIED': { background: '#e0f5f1', color: '#2d7a6c' },
      'JOB-APPLIED': { background: '#e0f5f1', color: '#2d7a6c' },
      'ACCEPTED': { background: '#e0f5f1', color: '#2d7a6c' },
      'INITIATED': { background: '#e0f5f1', color: '#2d7a6c' },
      'ACTIVE': { background: '#e0f5f1', color: '#2d7a6c' },
      'FILLED': { background: '#e0f5f1', color: '#2d7a6c' },
      'INTERVIEW-ACCEPTED': { background: '#e0f5f1', color: '#2d7a6c' },
      'OFFER-ACCEPTED': { background: '#e0f5f1', color: '#2d7a6c' },
      'SHORTLISTED': { background: '#d9eefb', color: '#1d6fa7' },
      'INTERVIEW SCHEDULED': { background: '#d9eefb', color: '#1d6fa7' },
      'CANDIDATE-SHORTLISTED': { background: '#daf3f3', color: '#1d7576' },
      'INTERVIEW-SCHEDULED': { background: '#dffbdf', color: '#1a8d19' },
      'SELECTED': { background: '#d8f5d8', color: '#0a5f0b' },
      'CANDIDATE-SELECTED': { background: '#d8f5d8', color: '#0a5f0b' },
      'OFFERED': { background: '#f3f9d3', color: '#748500' },
      'ONBOARDED': { background: '#edffd2', color: '#5c8c0d' },
      'PREONBOARDED': { background: '#d6f5fa', color: '#0d7182' },
      'PRE-ONBOARDED': { background: '#d0f9ef', color: '#039171' },
      'REJECTED': { background: '#ffe5e5', color: '#d40000' },
      'PROFILE-REJECTED': { background: '#ffe5e5', color: '#d40000' },
      'INTERVIEW REJECTED': { background: '#ffe5e5', color: '#d40000' },
      'INTERVIEW-REJECTED': { background: '#ffe5e5', color: '#d40000' },
      'INTERVIEW-FAILED': { background: '#ffe5e5', color: '#d40000' },
      'OFFER DECLINED': { background: '#ffe5e5', color: '#d40000' },
      'OFFER-DECLINED': { background: '#ffe5e5', color: '#d40000' },
      'WITHDRAW': { background: '#ffe5e5', color: '#d40000' },
      'CANDIDATE-WITHDRAWN': { background: '#ffe5e5', color: '#d40000' },
      'CANDIDATE BACKOUT': { background: '#ffe5e5', color: '#d40000' },
      'INACTIVE': { background: '#ffe5e5', color: '#d40000' },
      'BLOCKED': { background: '#ffe5e5', color: '#d40000' },
      'UNAVAILABLE': { background: '#ffe5e5', color: '#d40000' },
      'DRAFTED': { background: '#ffe5e5', color: '#d40000' },
      'OFFER WITHDRAWN': { background: '#ffe0e0', color: '#a00100' },
      'OFFER-WITHDRAWN': { background: '#ffe0e0', color: '#a00100' },
      'RESUME-PENDING': { background: '#fff0d6', color: '#b37400' },
      'ON HOLD': { background: '#ffeae5', color: '#cc6a51' },
      'CLOSED': { background: '#eeeeee', color: '#5a5a5a' },
    };

    return statusColorMap[normalized] || { background: '#e0f5f1', color: '#2d7a6c' };
  }

  // Get stage label
  getStageLabel(status: string): string {
    if (!status) return 'Applied';

    const labelMap = {
      'JOB-APPLIED': 'Applied',
      'CANDIDATE-SHORTLISTED': 'Shortlisted',
      'INTERVIEW-SCHEDULED': 'Interview Scheduled',
      'INTERVIEW-ACCEPTED': 'Interview Accepted',
      'CANDIDATE-SELECTED': 'Selected',
      'OFFERED': 'Offered',
      'OFFER-ACCEPTED': 'Offer Accepted',
      'ONBOARDED': 'Onboarded',
      'PRE-ONBOARDED': 'Pre-Onboarded',
      'REJECTED': 'Rejected',
      'INTERVIEW-REJECTED': 'Interview Rejected',
      'OFFER-DECLINED': 'Offer Declined',
      'OFFER-WITHDRAWN': 'Offer Withdrawn'
    };

    // Unmapped statuses (L1/L2/L3-INTERVIEW etc.) must still read as
    // 'L1 Interview' — the old replace(/-/g,' ') fallback kept them ALL CAPS.
    return labelMap[status] || status
      .replace(/[-_]+/g, ' ')
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  // V2-parity label for the Candidates Invited / Resume Requests
  // status pill (see candidate-card.component.html:414-429). The
  // invitation flow has its own vocabulary (INITIATED / ACCEPTED /
  // INTETEREST_SHOWN) that does not appear in `getStageLabel`'s
  // applicant-pipeline map, so we resolve it here.
  getInvitedStatusLabel(status: string): string {
    if (!status) return '';
    const map = {
      'INITIATED': 'Invited',
      'INTETEREST_SHOWN': 'Invited',
      'ACCEPTED': 'Applied'
    };
    return map[status] || this.getStageLabel(status);
  }

  // Resolve the resume-request status for THIS job. findResumeRequestsForTheJob
  // rows don't carry `currentStatus` — the request status arrives as
  // `resumeRequestLogs[]` (one entry per requesting job, with jobId + status)
  // plus a top-level `resumeRequestStatus`. A candidate can have requests from
  // several jobs, so prefer the log entry matching the open job and fall back
  // to the top-level field.
  getResumeRequestStatus(candidate: any): string {
    const log = (candidate?.resumeRequestLogs || [])
      .find((l: any) => l?.jobId === this.jobDetails?.jobId);
    return log?.status || candidate?.resumeRequestStatus || '';
  }
  private sortResumeRequestsIfNeeded(): void {
  if (this.pageName !== 'resume-requests' || !Array.isArray(this.response)) return;
 
  this.response.sort((a: any, b: any) => {
    const isPendingA = this.getResumeRequestStatus(a) === 'INITIATED';
    const isPendingB = this.getResumeRequestStatus(b) === 'INITIATED';
    if (isPendingA !== isPendingB) return isPendingA ? -1 : 1;
 
    const dateA = new Date(this.getResumeRequestLogDate(a)).getTime();
    const dateB = new Date(this.getResumeRequestLogDate(b)).getTime();
    return dateB - dateA;
  });
}
 
private getResumeRequestLogDate(candidate: any): string {
  const log = (candidate?.resumeRequestLogs || [])
    .find((l: any) => l?.jobId === this.jobDetails?.jobId);
  return log?.date || candidate?.updatedOn || candidate?.createdOn;
}

  // V2-parity labels for the Resume Requests pill — the resume-request
  // interaction has its own vocabulary (candidate-job-details.component.html
  // RESUME_SENT block): INITIATED → "Resume pending", COMPLETED /
  // RESUME_UPDATED → "Resume attached". Anything else (row progressed into
  // the applicant pipeline) falls through to the invited/pipeline labels.
  getResumeRequestStatusLabel(status: string): string {
    if (!status) return '';
    const map = {
      'INITIATED': 'Resume Pending',
      'COMPLETED': 'Resume Attached',
      'RESUME_UPDATED': 'Resume Attached'
    };
    return map[status] || this.getInvitedStatusLabel(status);
  }

  // INITIATED must use the amber RESUME-PENDING palette (V2's
  // `getStyle('RESUME-PENDING')` at candidate-job-details.component.html:51),
  // not the green that getStageStyle maps INITIATED to for invitations.
  getResumeRequestStatusStyle(status: string): { background: string; color: string } {
    if (status === 'INITIATED') return this.getStageStyle('RESUME-PENDING');
    return this.getStageStyle(status);
  }

  // Candidate-availability badge label — mirrors the V2 `large` view at
  // candidate-card.component.html:414-429 which maps the raw `status`
  // field to the user-facing availability vocabulary
  // (ACTIVE → Available, INACTIVE → Unavailable, BLOCKED → Blocked,
  // DRAFTED → Draft). Returns empty string for any other value so the
  // badge renders only for these four states.
  getCandidateStatusLabel(status: string): string {
    if (!status) return '';
    const map = {
      'ACTIVE': 'Available',
      'INACTIVE': 'Unavailable',
      'BLOCKED': 'Blocked',
      'DRAFTED': 'Draft'
    };
    return map[status] || '';
  }

  // Check if status is excluded from dropdown
  // isExcludedStatus(status: string): boolean {
  //   const excludedStatuses = ['REJECTED', 'INTERVIEW-REJECTED', 'OFFER-DECLINED', 'OFFER-WITHDRAWN','ONBOARDED'];
  //   return excludedStatuses.includes(status);
  // }

  //I commented due to some issues found in the dropdown
  isExcludedStatus(status: string): boolean {
    if (this.filterApplicantData) {
      const find = this.filterApplicantData.JOB_APPLICATION_BLOCKING_ACTIONS.listItems &&
        this.filterApplicantData.JOB_APPLICATION_BLOCKING_ACTIONS.listItems.find(x => x.itemId === status);
      return find ? true : false;
    }
  }

  // Get filtered statuses for dropdown
  // filterApplicant(currentStatus: string): any[] {
  //   // This should return the list of available statuses based on current status
  //   // For now, return a basic list - parent component will need to provide this
  //   const allStatuses = [
  //     { item: 'Shortlisted' },
  //     { item: 'Interview Scheduled' },
  //     { item: 'Interview Accepted' },
  //     { item: 'Selected' },
  //     { item: 'Offered' },
  //     { item: 'Onboarded' },
  //     { item: 'Rejected' }
  //   ];
  //   return allStatuses;
  // }

  /**
   * Applicant-page lock for Pre-Onboarded / Onboarded.
   *
   * Onboarding is owned by the Contract Manager page, so the Applicants list
   * must never let ANY viewer (job owner, first upstream, candidate owner)
   * actually set those two statuses. They still render in the dropdown — the
   * user asked for them to stay visible — but greyed out and non-clickable at
   * every stage. Implemented as a template predicate rather than a flag baked
   * into filterApplicant()'s return value so the *ngFor keeps handing back the
   * same object references on each change-detection pass (cloning there would
   * re-create the menu DOM every cycle).
   */
  isStatusLocked(statusData: any): boolean {
    const itemId = typeof statusData === 'string' ? statusData : statusData?.itemId;
    return GigsumoConstants.isContractManagedApplicantStatus(itemId);
  }

  isWait: boolean = false;
  filterApplicant(data: any, nextAllowedStatuses?: string[]) {
    console.log('=== filterApplicant DEBUG ===');
    console.log('Input data:', data);
    console.log('filterApplicantData exists:', !!this.filterApplicantData);
    console.log('filterApplicantData:', this.filterApplicantData);

    // Backend role guard — empty array means view-only.
    if (nextAllowedStatuses && Array.isArray(nextAllowedStatuses) && nextAllowedStatuses.length === 0) {
      return [];
    }
    if (this.filterApplicantData) {
      // INTERVIEW-REJECTED (Reject Interview) is now a backend-permitted transition
      // (candidate owner, at INTERVIEW-SCHEDULED). No longer suppressed here — the
      // dropdown is filtered by the backend's nextAllowedStatuses below, so the option
      // surfaces only where the backend actually accepts it.
      const arrs: Array<any> = [...this.filterApplicantData.JOB_APPLICATION_BUYER_ACTIONS.listItems];

      // Backend's nextAllowedStatuses is the authoritative state machine
      // (CandidateService.DIRECT_APPLY_OWNER_NEXT / firstUpstreamAllowed). Use it
      // directly so the dropdown shows every status the backend will accept —
      // including INTERVIEW-REJECTED at INTERVIEW-ACCEPTED. Skips the legacy
      // order/wait-flow logic below which would otherwise drop valid options.
      console.log('[filterApplicant-DEBUG] currentStatus:', data,
          'nextAllowedStatuses:', nextAllowedStatuses,
          'arrs.itemIds:', arrs.map((x: any) => x.itemId));
      if (nextAllowedStatuses && Array.isArray(nextAllowedStatuses) && nextAllowedStatuses.length > 0) {
        let result = arrs.filter((_x: any) => nextAllowedStatuses.includes(_x.itemId));

        // L1/L2/L3 Interview options must stay hidden until the candidate has
        // reached INTERVIEW-ACCEPTED — backend's nextAllowedStatuses can list
        // them earlier (e.g. at APPLIED), so guard here too, not just in the
        // legacy fallback below.
        const interviewAcceptedStatuses = ['INTERVIEW-ACCEPTED', 'INTERVIEW_ACCEPTED', 'INTERVIEW ACCEPTED'];
        const l1l2l3Statuses = ['L1-INTERVIEW', 'L1_INTERVIEW', 'L1 INTERVIEW', 'L2-INTERVIEW', 'L2_INTERVIEW', 'L2 INTERVIEW', 'L3-INTERVIEW', 'L3_INTERVIEW', 'L3 INTERVIEW'];
        const isAfterInterviewAccepted = interviewAcceptedStatuses.includes(data) || l1l2l3Statuses.includes(data);
        if (!isAfterInterviewAccepted) {
          result = result.filter((_x: any) => !l1l2l3Statuses.includes(_x.itemId));
        }

        console.log('[filterApplicant-DEBUG] BACKEND-DRIVEN result:', result.map((x: any) => x.itemId));
        // Show each entry's display label — if any entry's `item` field is null/empty
        // the template renders {{statusData?.item}} as a blank <li>, which looks like
        // a "missing" option in the dropdown.
        console.log('[filterApplicant-DEBUG] BACKEND-DRIVEN result FULL:',
            result.map((x: any) => ({ itemId: x.itemId, item: x.item, value: x.value, order: x.order })));
        return result;
      }
      console.log('[filterApplicant-DEBUG] FALLBACK to legacy order-based logic — nextAllowedStatuses is empty/missing');

      const order = arrs.find(_x => _x.itemId === data)!.order;
      const lastValue: Array<any> = [];
      //  for RECRUITER ACTION

      const finalData: Array<any> = arrs.reduce((acc: Array<any>, current, _index, arr) => {

        const isAllWait = arr.some(pp => pp.wait === true);

        const find = acc.find(_d => _d.itemId === current.itemId);
        const next = arr.indexOf(current);
        const offer = arr.filter(o => (o.order === undefined || o.order === null));

        if (find === undefined) {

          const nextData = arr[next + 1];

          if (nextData && current.item != null && data === current.itemId) {
            if ((nextData.item === undefined || nextData.item === null)) {
              this.isWait = true;
              return acc;
            }
            else if (nextData.item != undefined || nextData.item != null) {
              this.isWait = false;
              return acc;
            }
          }

          if (current && current.item != undefined && current.order != undefined) {
            acc.push(current);
          }
          else if (!lastValue.includes(offer) && (current.order === undefined || current.order === null)) {
            lastValue.push(...offer);
          }

          if (this.isWait) {
            acc = [];
            if (!acc.includes(offer)) {
              acc.push(...offer);
            }
          }
        }

        return acc;
      }, []);

      let final = finalData.filter(_x => _x.order > order);
      if (!this.isWait) {
        final.push(...lastValue);
      }

      // L1/L2/L3 Interview statuses must stay hidden until the candidate has
      // accepted the interview. INTERVIEW-SCHEDULED only unlocks them once the
      // opponent moves the candidate to INTERVIEW-ACCEPTED (or later L1/L2/L3
      // states themselves). Mirrors candidate-card.component.ts:869-877.
      const interviewAcceptedStatuses = ['INTERVIEW-ACCEPTED', 'INTERVIEW_ACCEPTED', 'INTERVIEW ACCEPTED'];
      const l1l2l3Statuses = ['L1-INTERVIEW', 'L1_INTERVIEW', 'L1 INTERVIEW', 'L2-INTERVIEW', 'L2_INTERVIEW', 'L2 INTERVIEW', 'L3-INTERVIEW', 'L3_INTERVIEW', 'L3 INTERVIEW'];
      const isAfterInterviewAccepted = interviewAcceptedStatuses.includes(data) || l1l2l3Statuses.includes(data);
      if (!isAfterInterviewAccepted) {
        final = final.filter(_x => !l1l2l3Statuses.includes(_x.itemId));
      }

      // Role-based intersection: keep only statuses the backend says the viewer may set.
      let roleFiltered: Array<any> | null = null;
      if (nextAllowedStatuses && Array.isArray(nextAllowedStatuses) && nextAllowedStatuses.length > 0) {
        final = final.filter(_x => nextAllowedStatuses.includes(_x.itemId));
        roleFiltered = finalData.filter((_x: any) => nextAllowedStatuses.includes(_x.itemId));
        if (!isAfterInterviewAccepted) {
          roleFiltered = roleFiltered.filter((_x: any) => !l1l2l3Statuses.includes(_x.itemId));
        }
      }

      // Once a candidate has reached the offer stage, "Withdraw Offer" must
      // remain available even if backend role guards (or order-based filtering)
      // would otherwise drop it. Reinstate it for OFFERED and any post-offer stage.
      const offerOrAbove = ['OFFERED', 'OFFER-ACCEPTED', 'PRE-ONBOARDED', 'PREONBOARDED'];
      if (offerOrAbove.includes(data)) {
        const withdrawItem = arrs.find((x: any) => x.itemId === 'OFFER-WITHDRAWN')
          || { itemId: 'OFFER-WITHDRAWN', item: 'Withdraw Offer', value: 'Withdraw Offer' };
        if (!final.some((x: any) => x.itemId === 'OFFER-WITHDRAWN')) {
          final.push(withdrawItem);
        }
        if (roleFiltered && !roleFiltered.some((x: any) => x.itemId === 'OFFER-WITHDRAWN')) {
          roleFiltered.push(withdrawItem);
        }
        if (!finalData.some((x: any) => x.itemId === 'OFFER-WITHDRAWN')) {
          finalData.push(withdrawItem);
        }
      }

      if (roleFiltered) {
        console.log('Role-filtered result:', this.isWait ? roleFiltered : final);
        return this.isWait ? roleFiltered : final;
      }

      console.log('Final filtered result:', this.isWait ? finalData : final);
      return this.isWait ? finalData : final;
    }
    // V2 parity (candidate-card.component.ts filterApplicant). When the
    // listvalue payload hasn't loaded, return [] — the prior hardcoded
    // fallback shipped non-canonical itemIds (`SHORTLISTED`,
    // `INTERVIEW_SCHEDULE`) that don't match the server enum, so clicks on
    // those rows pushed invalid statuses through `candidates/updatejobAppliedStatus`.
    return [];
  }

  // Event emitters for parent component
  @Output() onCandidateClick = new EventEmitter<any>();
  @Output() onToggleSkills = new EventEmitter<any>();
  @Output() onViewProfile = new EventEmitter<any>();
  @Output() onDownloadResume = new EventEmitter<any>();
  @Output() onSendMessage = new EventEmitter<any>();
  @Output() onOpenDocuments = new EventEmitter<any>();
  @Output() onShareProfile = new EventEmitter<any>();
  @Output() onScheduleInterview = new EventEmitter<any>();
  @Output() onAddNotes = new EventEmitter<any>();
  @Output() onStatusUpdate = new EventEmitter<any>();
  @Output() showDetails = new EventEmitter<any>();
  @Output() inviteClicked = new EventEmitter<any>();
  // ── Status-update emissions, parity with candidate-card row-design ──
  // Fires AFTER the API call resolves. Parent's `statusUpdateTriggered`
  // handler calls `getApplicantsCounts(jobDetails)` to refresh stage
  // counters; `pushtoparentcomponent` re-maps `response[]` setting
  // `isSelected` + new `activityDate` so the row visually refreshes
  // without a full reload.
  @Output() statusUpdated = new EventEmitter<any>();
  @Output() jobapp_to_newjob_to_atvity = new EventEmitter<any>();

  // Methods that call parent via events
  route(item: any): void {
    this.onCandidateClick.emit(item);
  }

  /** Row-level eye icon → open slider on the Candidate Profile tab. */
  open(item: any): void {
    this.onViewProfile.emit(item);
    this.slidePanelService.openCandidateDetail(item, this.jobDetails?.jobId, {
      variant: 'v3',
      initialTab: 'profile',
      pageFrom: this.pageName,
      // Hide the slider's AI Insights tab + header match ring (same gating
      // as the row's match badge):
      //   resume-requests   — no AI matching runs for that flow.
      //   candidate-applied — own submissions on someone else's job; the
      //                       match % is the job owner's evaluation.
      hideAiInsights: this.pageName === 'resume-requests' || this.pageName === 'candidate-applied',
      jobData: this.jobDetails
    });
  }

  /** Match-badge click → open slider on the AI Insights tab.
   *  `$event.stopPropagation()` keeps the row's own click/navigate handlers
   *  from firing so the slider "takes over" the interaction. */
  // Match colour tier by score: green >=75, amber 40-74, red <40 (empty when no
  // score → the .no-score grey styling applies). Mirrors .jc-v3-c-match-badge
  // {,.amber,.red} in job-center-v3.scss and the header/AI-insights donut.
  matchTier(candidate: any): string {
    const score = candidate?.score || candidate?.matchingScore;
    if (score == null) return '';
    if (score >= 75) return 'green';
    if (score >= 40) return 'amber';
    return 'red';
  }

  openMatch(item: any, ev?: Event): void {
    if (ev) { ev.stopPropagation(); ev.preventDefault(); }
    this.slidePanelService.openCandidateDetail(item, this.jobDetails?.jobId, {
      variant: 'v3',
      initialTab: 'ai',
      pageFrom: this.pageName,
      jobData: this.jobDetails
    });
  }

  async downloadResume(item: any): Promise<void> {
    this.onDownloadResume.emit(item);
    if (this.isClicked) return;
    if (!item?.resumes?.length) return;

    const defaultResume = item.resumes.find((r: any) => r.isDefault) || item.resumes[0];
    if (!defaultResume?.fileId) return;

    const fileId = defaultResume.fileId;
    const userId = localStorage.getItem('userId');
    const fileName = this.cleanResumeFileName(defaultResume?.fileName || defaultResume?.name);

    this.isClicked = true;

    // APPLICANTS MENU ONLY - and this runs FIRST, before the resumeDownloaded check.
    // The job owner invited this candidate for this job, which already consumed one
    // ACTIVE_INTERACTION; the candidate then applied and appears here. So the resume is
    // already paid for and the plan check must be skipped entirely.
    //
    // It has to come before `!item.resumeDownloaded`, not inside it: that flag only records
    // DOWNLOADS, so an invited candidate whose resume was downloaded once short-circuited to
    // the else branch and the check never ran at all.
    // Scoped by pageName so Applied / Resume Requests / other lists are untouched.
    const isApplicantsMenu = this.pageName === 'job-applicants' && !!this.jobDetails?.jobId;

    if (isApplicantsMenu
        && await this.planService.isInterestShownForJob(item.candidateId, this.jobDetails.jobId)) {
      this.afterPlanValidationDownloadResume(item.candidateId, userId, item.createdBy, fileId, fileName);
      // Released here because this return skips the 5-second reset at the end.
      this.isClicked = false;
      return;
    }

    // In the APPLICANTS menu an INTEREST_NOT_FOUND answer means the slot check ALWAYS runs -
    // including for a resume this viewer downloaded before. The interest lookup, not
    // `resumeDownloaded`, is what decides whether the pair was already paid for there.
    // Every other screen keeps the original short-circuit. Your own candidate is always free.
    const needsSlotCheck = item.createdBy != userId
        && (isApplicantsMenu || !item.resumeDownloaded);

    if (needsSlotCheck) {
      const userData = await this.planService.UPDATE_USERDETAILS('ACTIVE_INTERACTIONS');
      let availablePoints = null, actualPoints = null, utilizedPoints = null, promotional = null;
      if (userData.userDetail?.userPlanAndBenefits?.benefitsUsages) {
        userData.userDetail.userPlanAndBenefits.benefitsUsages.forEach((ele: any) => {
          if (ele.benefitKey === 'ACTIVE_INTERACTIONS') {
            availablePoints = ele.available; actualPoints = ele.actual;
            utilizedPoints = ele.utilized; promotional = ele.promotional;
          }
        });
      }
      if (userData.isExpired) {
        const popupType: 'PAY' | 'LIMIT' | 'UPGRADE' = ['Starter', 'Growth', 'ScaleX'].includes(userData.membershipType) ? 'LIMIT' : 'PAY';
        this.planService.expiredPopup('ACTIVE_INTERACTIONS', popupType, null, null, null, null, availablePoints, actualPoints, utilizedPoints, promotional);
        this.isClicked = false;
        return;
      }
      item.resumeDownloaded = true;
      this.afterPlanValidationDownloadResume(item.candidateId, userId, item.createdBy, fileId, fileName);
    } else {
      this.afterPlanValidationDownloadResume(item.candidateId, userId, item.createdBy, fileId, fileName);
    }
    setTimeout(() => { this.isClicked = false; }, 5000);
  }

  /** Backend stores resumes on disk as `<uuid>_<originalFilename>.<ext>` and
   *  echoes that disk name back on `resume.fileName`. Strip the leading UUID
   *  segment so the saved file uses the candidate-friendly original name
   *  (and keeps its extension — without it the OS treats the download as
   *  plain text). Mirrors the helper in `cp-resume-card.component.ts`. */
  private cleanResumeFileName(raw: string | null | undefined): string {
    const s = (raw || '').toString().trim();
    if (!s) return '';
    const uuidPrefix = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}_/;
    return uuidPrefix.test(s) ? s.replace(uuidPrefix, '').trim() : s;
  }

  afterPlanValidationDownloadResume(candidateId: any, userId: any, createdBy: any, fileId: any, fileName?: string) {
    const endpoint = `candidates/resumeDownload/${candidateId}/${userId}/${createdBy}/${fileId}`;
    this.apiService.queryBlob(endpoint).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      const blob = new Blob([res]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName || 'resume.pdf';
      document.body.append(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 0);
    });
  }

  // Chat functionality (copied from candidate-card)
  async sendMessage(item: any): Promise<void> {
    // Call the interaction check method before opening message
    await this.openMessagecheckInteraction(item);
  }

  // Check connection status before opening chat
  checkConnectionStatus(values: any): Observable<any> {
    const apiURL = 'user/checkConnectionStatus';
    const params = new HttpParams()
      .set('userId', this.userId)
      .set('connnectorId', values.createdBy);

    const urlWithParams = `${apiURL}?${params.toString()}`;
    return this.apiService.query(urlWithParams);
  }

  // Check active interactions before opening chat
  async openMessagecheckInteraction(seletedItem: any, connectionTemplate?: TemplateRef<any>) {
    // Universal first-interaction: exist-first — skip the ACTIVE_INTERACTIONS gate
    // when this candidate's interaction was already consumed (existing chat / prior
    // invite / resume-request — backend `alreadyConsumed`). Fails safe to gating.
    if (await this.planService.isInteractionAlreadyConsumed(seletedItem?.candidateId, 'CANDIDATE')) {
      this.chatuser(seletedItem);
      return;
    }
    try {
      this.util.startLoader();
      const userData = await this.planService.UPDATE_USERDETAILS("ACTIVE_INTERACTIONS");
      let { userDetail, isExpired, membershipType } = userData;
      let availablePoints = null;
      let actualPoints = null;
      let utilizedPoints = null;
      let promotional = null;

      if (userDetail && userDetail.userPlanAndBenefits && userDetail.userPlanAndBenefits.benefitsUsages) {
        userDetail.userPlanAndBenefits.benefitsUsages.forEach(ele => {
          if (ele.benefitKey == 'ACTIVE_INTERACTIONS') {
            availablePoints = ele.available;
            actualPoints = ele.actual;
            utilizedPoints = ele.utilized;
            promotional = ele.promotional;
          }
        });
      } else {
        this.util.stopLoader();
        this.chatuser(seletedItem);
        return;
      }

      if (isExpired) {
        let popupType: "PAY" | "LIMIT" | "UPGRADE" = "PAY";

        if (['Starter','Growth','ScaleX'].includes(membershipType)) {
          popupType = "LIMIT";
        }

        this.planService.expiredPopup("ACTIVE_INTERACTIONS", popupType, null, null, null, null, availablePoints, actualPoints, utilizedPoints, promotional);
      } else {
        this.util.stopLoader();
        this.chatuser(seletedItem);
      }
    } catch (error) {
      console.error('Error checking interaction:', error);
    } finally {
      setTimeout(() => {
        this.util.stopLoader();
      }, 1500);
    }
  }

  // Open chat user (main chat logic)
  chatuser(seletedItem: any, connectionModal?: TemplateRef<any>) {
    // If user owns the candidate, open message directly
    if (seletedItem.createdBy == this.userId) {
      this.openMessage(seletedItem);
      return;
    }

    // Check connection status for non-owned candidates
    this.checkConnectionStatus(seletedItem).pipe(takeUntil(this.destroy$)).subscribe(response => {
      if (seletedItem.isFeatured != undefined && seletedItem.isFeatured == true) {
        this.openMessage(seletedItem);
      } else if ((seletedItem.isFeatured == false || seletedItem.isFeatured == undefined) && seletedItem.createdBy != this.userId) {
        // Open message regardless of connection status
        this.openMessage(seletedItem);
      }
    }, error => {
      console.error('Error checking connection status:', error);
      // Still open message even if connection check fails
      this.openMessage(seletedItem);
    });
  }

  // Open the message widget
  openMessage(seletedItem: any) {
    this.messagemodelflag = false;

    // Set owner flag
    if (seletedItem.createdBy == this.userId) {
      seletedItem.owner = true;
    } else {
      seletedItem.owner = false;
    }

    setTimeout(() => {
      seletedItem.userId = seletedItem.createdBy;
      this.seletedChatItem = seletedItem;
      this.messageData = [];
      this.messagemodelflag = true;
      seletedItem.groupType = "CANDIDATE";
      seletedItem.messageType = "CANDIDATE";
      seletedItem.id = seletedItem.candidateId;
      this.messageData = seletedItem;
    }, 1000);
  }

  // Close message widget
  closeMessage(event: any) {
    this.messagemodelflag = false;
  }

  openDocuments(item: any): void {
    this.onOpenDocuments.emit(item);
  }

  shareProfile(item: any): void {
    this.onShareProfile.emit(item);
  }

  scheduleInterview(item: any): void {
    this.onScheduleInterview.emit(item);
  }

  addNotes(item: any): void {
    this.onAddNotes.emit(item);
  }

  // Negative-status set — typo (`negativeStauses`) kept intentionally to
  // match candidate-card row-design's source so a future codemod that
  // moves this constant into a shared util can find both call-sites.
  negativeStauses = [
    'REJECTED', 'PROFILE-REJECTED', 'INTERVIEW-REJECTED', 'INTERVIEW-FAILED',
    'OFFER-DECLINED', 'CANDIDATE-WITHDRAWN', 'OFFER-WITHDRAWN'
  ];

  /** Stage-dropdown click handler — verbatim port of
   *  `candidate-card.component.ts:validateNegativeStatus()`
   *  (see candidate-card-row-design-validateNegativeStatus.md §1).
   *
   *  In the row-design source the spec references `this.candidateval`
   *  (the per-host candidate). In a list view there is no single
   *  `candidateval`; each row passes its own candidate in as `value`.
   *  Two `this.candidateval?.engagementType` reads in the spec are
   *  rewritten as `value?.engagementType` here so the JOB_SEEKER
   *  branch evaluates against the row that was actually clicked. */
  validateNegativeStatus(datas: any, value: any, interviewPayrateTemplate?: TemplateRef<any>) {
    console.warn('status to update : ', datas.itemId);

    const targetStatus = datas.itemId;
    // Read the candidate's current stage so we can decide if the gate
    // has already been crossed (see "Payrate gate" block below).
    const currentStatus = (value?.currentStatus || '').toString().toUpperCase();

    const membershipType = localStorage.getItem('MemberShipType');
    const userType = localStorage.getItem('userType');
    const isFreeOrJobSeeker = membershipType === 'Free' || userType === 'JOB_SEEKER';

    // ----- TEMPORARY free-plan gate (buyer/job-owner side). Block a FREE-plan job owner from
    // advancing a candidate to Selected or any later stage. Checked EARLY (before the payrate
    // modal opens, which is what creates the contract-candidate record) using the already-loaded
    // membershipType. Only an explicit 'Free' plan is blocked, so Business-plan (paid) users are
    // never affected. Full free-plan handling is a separate effort. -----
    const planGatedStages = ['SELECTED', 'CANDIDATE-SELECTED', 'OFFERED', 'OFFER-ACCEPTED',
      'PRE-ONBOARDED', 'PREONBOARDED', 'ONBOARDED'];
    if (membershipType === 'Free' && planGatedStages.includes((targetStatus || '').toString().toUpperCase())) {
      Swal.fire({
        icon: 'warning',
        title: 'Business Plan Required',
        html: "You can't proceed with the next process on the Free plan. Please purchase a Business Plan to continue.",
        confirmButtonText: 'OK'
      });
      return;
    }

    // JOB_SEEKER with non-Corp-to-Corp: no payrate popup at ANY stage, just update status directly
    if (userType === 'JOB_SEEKER') {
      const engType = value?.engagementType;
      const isCorpToCorp = engType === 'Corp to Corp' || engType === 'Corp To Corp';
      if (!isCorpToCorp) {
        if (this.negativeStauses.includes(targetStatus)) {
          const swalWithBootstrapButtons = Swal.mixin({
            customClass: { confirmButton: 'btn btn-success', cancelButton: 'btn btn-danger' },
            buttonsStyling: false
          });
          swalWithBootstrapButtons.fire({
            title: 'Are you sure you want to ' + this.prepareMessage(targetStatus) + '?',
            html: `Once you ${this.prepareMessage(targetStatus)}, the candidate will no longer be considered for this job.<br><p>This action cannot be undone.</p>`,
            icon: 'warning', showCancelButton: true, confirmButtonText: 'Yes', cancelButtonText: 'Cancel', reverseButtons: true
          }).then(result => { if (result.isConfirmed) { this.updatestatus(datas, value); } });
        } else {
          this.updatestatus(datas, value);
        }
        return;
      }
    }

    // ── Payrate gate ──────────────────────────────────────────────────
    // The Rate-Confirmation modal is what creates the contract-candidate
    // record on the backend, so it should fire ONCE per candidate — only
    // when the candidate is *first* crossing into a "rated" stage. Earlier
    // logic opened the modal for every payrate-adjacent status change
    // (INTERVIEW-SCHEDULED → ONBOARDED), which re-prompted the recruiter
    // every time and tried to re-create a contract that already exists.
    //
    // Trigger stages   — opening the modal is the gate transition itself.
    // Past-gate stages — the candidate is already at or beyond the gate;
    //                    the contract exists, so subsequent moves
    //                    (incl. PRE-ONBOARDED / ONBOARDED) skip the modal.
    // Anything else    — never opens the modal (SHORTLISTED, INTERVIEW-
    //                    SCHEDULED, L1/L2/L3-INTERVIEW, etc).
    // Locked 2026-06-08 — payrate modal fires ONLY at OFFERED (mirror of the
    // candidate-card change). SELECTED / CANDIDATE-SELECTED targets bypass the
    // modal entirely and flow through normal updatestatus.
    const TRIGGER_STAGES = ['OFFERED'];
    // VALIDATE_STAGES — call /validate-b2b-connection without opening the
    // PayRate modal. Free-plan gate (10006) was removed in the 2026-05-20
    // refactor, so INTERVIEW-* / L1-L3-INTERVIEW no longer need any backend
    // call from the buyer-side view — they just push the status straight
    // through. Only OFFER-ACCEPTED / PRE-ONBOARDED / ONBOARDED still gate
    // on B2B, and the supplier-side flow (job-card.component.jobupdatestatus)
    // is the primary owner of OFFER-ACCEPT. We keep the three onboarding
    // statuses here as a defensive net for buyer-side direct transitions.
    const VALIDATE_STAGES = [
      'OFFER-ACCEPTED',
      'PRE-ONBOARDED', 'PREONBOARDED',
      'ONBOARDED'
    ];
    // Locked 2026-06-08 — SELECTED / CANDIDATE-SELECTED dropped from PAST_GATE
    // so a candidate currently at SELECTED can still trigger the modal on the
    // SELECTED→OFFERED transition.
    const PAST_GATE_STAGES = [
      'OFFERED', 'OFFER-ACCEPTED',
      'PRE-ONBOARDED', 'PREONBOARDED',
      'ONBOARDED'
    ];
    const offerAndAbove = ['OFFERED', 'OFFER-ACCEPTED', 'PRE-ONBOARDED', 'PREONBOARDED', 'ONBOARDED'];

    const isTriggerStage   = TRIGGER_STAGES.includes(targetStatus);
    const isValidateStage  = VALIDATE_STAGES.includes(targetStatus);
    const isPastGate       = PAST_GATE_STAGES.includes(currentStatus);
    const isOfferStageOrAbove = offerAndAbove.includes(targetStatus);

    if (isTriggerStage && !isPastGate) {
      // 2026-05-20: Open the payrate modal at SELECTED / CANDIDATE-SELECTED / OFFERED
      // for every candidate type (paid, free plan, JOB_SEEKER Corp-to-Corp). Free-plan
      // gate removed; B2B is no longer pre-flighted here — the modal opens regardless
      // of B2B status. The actual B2B/SUPERADMIN gating now happens at OFFER-ACCEPT
      // (see job-card.component.jobupdatestatus).
      if (interviewPayrateTemplate) {
        this.validateB2bAndOpenPayrate(datas, value, interviewPayrateTemplate);
        return;
      }
    }

    // Validate-only stages — call the API to surface the FREE-plan
    // job-owner block (10006). On success, no modal opens; status updates
    // proceed normally. On 10006, the validate handler shows the
    // "Purchase Plan" popup and the status change is aborted.
    if (isValidateStage) {
      this.validateB2bAndOpenPayrate(datas, value, null);
      return;
    }

    // validate negative status for confirmation
    if (this.negativeStauses.includes(datas.itemId)) {

      const swalWithBootstrapButtons = Swal.mixin({
        customClass: {
          confirmButton: 'btn btn-success',
          cancelButton: 'btn btn-danger'
        },
        buttonsStyling: false
      });

      swalWithBootstrapButtons.fire({
        title: 'Are you sure you want to ' + this.prepareMessage(datas.itemId) + '?',
        html: `Once you ${this.prepareMessage(datas.itemId)}, the candidate will no longer be considered for this job.<br>
        <p>This action cannot be undone.</p>`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'Cancel',
        reverseButtons: true
      }).then(result => {
        if (result.isConfirmed) {
          this.updatestatus(datas, value);
        }
      });
    } else {
      this.updatestatus(datas, value);
    }
  }

  /** Swal copy helper — verbatim port (md §2). The 5 conditions are
   *  exhaustive in the spec; any other status returns `undefined`,
   *  which is fine because `validateNegativeStatus` only calls this
   *  when the status is in `negativeStauses`. */
  prepareMessage(sts: any): string {
    if (sts == 'INTERVIEW-FAILED') {
      return 'reject interview';
    } else if (sts == 'OFFER-WITHDRAWN') {
      return 'withdraw offer';
    } else if (sts == 'PROFILE-REJECTED') {
      return 'reject profile';
    } else if (sts == 'CANDIDATE-WITHDRAWN') {
      return 'withdraw';
    } else if (sts == 'OFFER-DECLINED') {
      return 'decline offer';
    }
  }

  /** Wrapper used by other contract views — verbatim port (md §10).
   *  Delegates to `validateNegativeStatus` without the payrate modal
   *  ref so negative-status confirmation still fires but the RTR
   *  modal path falls through to direct update. */
  contractUpdateStatus(statusData: any, candidateVal: any): void {
    this.validateNegativeStatus(statusData, candidateVal);
  }

  openPayRate(candidateval: any, template: TemplateRef<any>) {
    this.validateB2bAndOpenPayrate(null, candidateval, template);
  }

  // ── §3 validateB2bAndOpenPayrate — verbatim port of md §3 ─────────────
  private validateB2bAndOpenPayrate(datas: any, value: any, template: TemplateRef<any>): void {
    const userId = localStorage.getItem('userId') || '';
    // openPayRate (manual "Pay Rate" button) passes datas=null — there's no
    // target status from a dropdown click, so fall back to the candidate's
    // current status. Without this the validate-b2b payload would omit
    // candidateCurrentStatus, and the backend can't tell SELECTED+ from
    // pre-OFFER and falls into the safe skipContractCreation branch — which
    // hides supplierBusinessId / b2bContactId from the response.
    const targetStatus = datas?.itemId
        || value?.currentStatus
        || value?.contractData?.candidateStatus
        || '';
    this.util.startLoader();
    this.apiService.query(`business/findUserRoleInBusiness/${userId}`).subscribe({
      next: (res: any) => {
        const businessId = res?.data?.businessData?.businessId;
        if (!businessId) {
          // JOB_SEEKER and Free-plan users may legitimately have no business
          // mapping. Both audiences are routed here by the payrate gate when
          // crossing into OFFERED+, so neither should be blocked by the
          // "Business not found" popup — fall through to the same path the
          // JOB_SEEKER bypass uses.
          const userType = localStorage.getItem('userType');
          const membershipType = localStorage.getItem('MemberShipType');
          const isFreeOrJobSeeker = userType === 'JOB_SEEKER' || membershipType === 'Free';
          if (isFreeOrJobSeeker) {
            this.util.stopLoader();
            // openPayRate button passes datas=null — there's no status to push,
            // so open the payrate modal instead of crashing in updatestatus.
            if (!datas) {
              this.openInterviewPayrateModal(datas, value, template);
              return;
            }
            this.updatestatus(datas, value);
            return;
          }
          // No business context — for the PayRate modal path, open the modal
          // anyway (backend resolves the IDs server-side). For the validate-
          // only path (template=null) just push the status; the modal would
          // freeze with a null TemplateRef.
          this.util.stopLoader();
          if (!template) {
            if (datas) {
              this.updatestatus(datas, value);
            }
            return;
          }
          this.b2bData = null;
          this.openInterviewPayrateModal(datas, value, template);
          return;
        }
        const candidateId = value.candidateId || value.contractData?.candidateId || '';
        const jobId = this.jobId || value.contractData?.jobId || '';
        this.contractCandidateService.validateB2bConnection(businessId, jobId, candidateId, targetStatus).subscribe({
          next: (b2bRes: any) => {
            this.util.stopLoader();
            // Validate-only path (no PayRate template): never open the modal.
            // On success push the status; on B2B-missing show the popup and
            // abort. Without this guard, modalService.show(null) freezes the UI.
            if (!template) {
              if (b2bRes?.code === '00000') {
                if (datas) {
                  this.updatestatus(datas, value);
                }
                return;
              }
              if (b2bRes?.code === '10002' || b2bRes?.code === '10003' || b2bRes?.code === '10004') {
                // 10004 = invite already pending acceptance → popup only, no route.
                // showNoB2bPopup handles 10004 messaging + suppresses the navigation CTA.
                this.contractCandidateService.showNoB2bPopup(b2bRes.code, b2bRes.message)
                  .then(result => {
                    if (result.isConfirmed && b2bRes.code === '10002') {
                      this.router.navigate(['/contracts/customers']);
                    }
                  });
                return;
              }
              if (b2bRes?.code === '10005') {
                Swal.fire({
                  icon: 'warning',
                  title: 'Organization Required',
                  html: b2bRes.message || 'Corp-to-Corp candidates must have an active organization.',
                  confirmButtonText: 'OK'
                });
                return;
              }

              if(b2bRes.code =='99996'){
                // Mandatory-document gate — shared wording with candidate-card so
                // the same blocker never reads two different ways.
                this.contractCandidateService.showMandatoryDocumentPopup();
                return;
              }
              Swal.fire({ icon: 'error', title: 'Validation Failed', text: b2bRes?.message || 'Unable to proceed with this status change.' });
              return;
            }
            // Backend may signal that no contract is needed (e.g. JOB_SEEKER
            // non-Corp-to-Corp early in flow). Honour that — skip the modal.
            if (b2bRes?.code === '00000' && b2bRes.data?.skipContractCreation) {
              this.updatestatus(datas, value);
              return;
            }
            // For 00000 we have rich b2bData; for 10002 / 10003 / 10005 we open
            // the modal anyway with null b2bData. The backend's createCandidate
            // accepts null b2bContactId and resolves it server-side, and the
            // "No B2B Connected" chip in Contract Management surfaces the gap.
            this.b2bData = b2bRes?.code === '00000' ? b2bRes.data : null;
            this.openInterviewPayrateModal(datas, value, template);
          },
          error: () => {
            this.util.stopLoader();
            if (!template) {
              // Validate-only path failed at the network layer — fall through
              // to the status update; backend has its own authorization guard.
              if (datas) {
                this.updatestatus(datas, value);
              }
              return;
            }
            this.b2bData = null;
            this.openInterviewPayrateModal(datas, value, template);
          }
        });
      },
      error: () => {
        this.util.stopLoader();
        if (!template) {
          if (datas) {
            this.updatestatus(datas, value);
          }
          return;
        }
        this.b2bData = null;
        this.openInterviewPayrateModal(datas, value, template);
      }
    });
  }

  /**
   * Locked 2026-06-08 — tier resolver for two-row payrate modal. Mirror of
   * candidate-card.resolveViewerTier — kept inline to avoid a shared service.
   */
  private resolveViewerTier(viewerBizId: string, job: any): any {
    const tiers = job?.tierPayRates || [];
    if (!tiers.length || !viewerBizId) return null;
    const owned = tiers.find((t: any) => t?.customerBusinessId
        && t.customerBusinessId.toLowerCase() === viewerBizId.toLowerCase());
    if (owned) return owned;
    const upstream = tiers.find((t: any) => t?.supplierBusinessId
        && t.supplierBusinessId.toLowerCase() === viewerBizId.toLowerCase());
    if (upstream) return upstream;
    // Locked 2026-06-10 — first-tier fallback removed. Let patchPayrateCeiling
    // fall through to JobDetails.targetRateTo + payType instead of silently
    // using the job-owner's tier as the "Submitted for" ceiling for every viewer.
    return null;
  }

  private patchPayrateCeiling(value: any): void {
    const viewerBizId = localStorage.getItem('myBusinessId')
        || localStorage.getItem('businessId') || '';
    const job = this.appliedJobDetails || value?.jobDetails;

    // Chain-first lookup (locked 2026-06-12): when the candidate has a
    // submittedForChain, the rate THIS viewer was submitted at sits in the latest
    // entry where customerBusinessId == viewer. That's the contractual ceiling for
    // the offer — you cannot offer the candidate more than your supplier quoted
    // you. Walks the chain from newest → oldest so rate revisions (append-only)
    // pick up the most recent value. Falls through to legacy tier/job rates when
    // no chain entry matches (direct apply OR pre-feature legacy applicant).
    let chainRate: number | null = null;
    let chainRateType: string | null = null;
    if (viewerBizId && Array.isArray(value?.submittedForChain) && value.submittedForChain.length > 0) {
      const viewerLower = viewerBizId.toLowerCase();
      for (let i = value.submittedForChain.length - 1; i >= 0; i--) {
        const e = value.submittedForChain[i];
        if (e && (e.customerBusinessId || '').toLowerCase() === viewerLower) {
          const parsed = parseFloat(String(e.submittedRate || ''));
          if (!isNaN(parsed) && parsed > 0) chainRate = parsed;
          if (e.submittedRateType) chainRateType = e.submittedRateType;
          break;
        }
      }
    }

    const tier = this.resolveViewerTier(viewerBizId, job);
    // Locked 2026-06-12 — three-tier ceiling read:
    //   submittedForChain entry (viewer == customer, latest) >
    //   tier match (per-supplier) >
    //   JobDetails.targetRateTo (job-level)
    const submittedToRate = chainRate ?? tier?.toPayRate ?? job?.targetRateTo ?? null;
    const submittedRateType = chainRateType ?? tier?.rateType ?? job?.payType ?? 'Hourly';
    // Locked 2026-06-10 — Offer For initial value mirrors Submitted For. Offer For
    // stays editable; Submitted For is read-only. Both rows pre-fill with the
    // same tier (or JobDetails fallback) value so the user adjusts Offer For from
    // a sensible default instead of starting blank.
    this.rtrformgrp.patchValue({
      submittedToRate,
      submittedRateType,
      offerRate: submittedToRate,
      offerRateType: submittedRateType,
      country: value?.country || '',
      termsandcondition: null
    });
    const offerCtrl = this.rtrformgrp.get('offerRate');
    if (offerCtrl) {
      // Removed 2026-06-12 — no max ceiling on offerRate. Sub-contractors mark up
      // the rate they were submitted at; product allows offers above the chain/tier
      // reference. submittedToRate is now a display hint, not an enforcement bound.
      const validators = [Validators.required, CustomValidator.validDecimal(), CustomValidator.checkWhiteSpace()];
      offerCtrl.setValidators(validators);
      offerCtrl.updateValueAndValidity({ emitEvent: false });
    }
  }

  // ── §4 openInterviewPayrateModal — verbatim port of md §4 ─────────────
  openInterviewPayrateModal(datas: any, value: any, template: TemplateRef<any>): void {
    console.log('openInterviewPayrateModal - candidate details:', value);
    console.log('openInterviewPayrateModal - job details:', { jobId: this.jobId, jobTitle: this.jobTitle, appliedJobDetails: this.appliedJobDetails });
    this.pendingOfferedData = datas;
    this.pendingOfferedCandidate = value;
    this.submitRTR = false;
    this.rtrformgrp.reset();
    this.patchPayrateCeiling(value);

    const modalOptions: ModalOptions = { class: 'modal-md', backdrop: 'static', keyboard: false };
    this.rtrModalRef = this.modalService.show(template, modalOptions);
  }

  // ── §5 openPayrateModal — verbatim port of md §5 ──────────────────────
  openPayrateModal(datas: any, value: any, template: TemplateRef<any>): void {
    this.pendingOfferedData = datas;
    this.pendingOfferedCandidate = value;
    this.submitRTR = false;
    this.rtrformgrp.reset();
    this.patchPayrateCeiling(value);
    const modalOptions: ModalOptions = { class: 'modal-md', backdrop: 'static', keyboard: false };
    this.rtrModalRef = this.modalService.show(template, modalOptions);
  }

  // ── §6 submitPayrate — verbatim port of md §6 ─────────────────────────
  submitPayrate(): void {
    this.submitRTR = true;
    if (this.rtrformgrp.invalid) return;

    // Locked 2026-06-08 — UI controls renamed to offerRate / offerRateType.
    // Wire format unchanged (targetRate + payType) so backend stays untouched.
    const payload = {
      jobId: this.jobId,
      candidateId: this.pendingOfferedCandidate.candidateId,
      candidateStatus: this.pendingOfferedData?.itemId || this.pendingOfferedCandidate.currentStatus,
      createdBy: this.userId,
      targetRate: parseFloat(this.rtrformgrp.value.offerRate),
      payType: this.rtrformgrp.value.offerRateType,
      customerBusinessId: this.b2bData?.customerBusinessId || '',
      supplierBusinessId: this.b2bData?.supplierBusinessId || '',
      b2bContactId: this.b2bData?.b2bContactId || '',
      isSubContracted: this.b2bData?.subContracted || false
    };

    console.table(payload);
    this.util.startLoader();
    this.contractCandidateService.createCandidate(payload).subscribe({
      next: (res: any) => {
        if (res.code === '00000') {
          // Capture before hidePayrateModal nulls them out
          const offeredData = this.pendingOfferedData;
          const offeredCandidate = this.pendingOfferedCandidate;
          this.hidePayrateModal();

          // Show auto-created external supplier notification if present
          if (res.data?.autoCreatedExternalBusiness) {
            const bizName = res.data.autoCreatedExternalBusiness.businessName || 'Unknown';
            Swal.fire({
              icon: 'info',
              title: 'External Supplier Created',
              text: `External Supplier auto-created: ${bizName}`,
              showConfirmButton: false,
              timer: 3000
            });
          }

          if (offeredData) {
            this.updatestatus(offeredData, offeredCandidate);
          } else {
            Swal.fire({
              icon: 'success',
              title: 'Payrate updated Successfully',
              showConfirmButton: false,
              timer: 2000
            });
            this.util.stopLoader();
          }
        } else {
          // After 2026-05-20, createContractCandidate accepts null b2bContactId
          // and resolves missing fields server-side, so the legacy 10001/10002/00001
          // "no business connection" branches no longer fire from this endpoint.
          // Any non-success here is a real error.
          this.util.stopLoader();
          this.hidePayrateModal();
          Swal.fire('Error', res.message || 'Failed to create contract candidate', 'error');
        }
      },
      error: () => {
        this.util.stopLoader();
        Swal.fire('Error', 'Failed to create contract candidate', 'error');
      }
    });
  }

  // ── §7 hidePayrateModal — verbatim port of md §7 ──────────────────────
  hidePayrateModal(): void {
    if (this.rtrModalRef) {
      this.rtrModalRef.hide();
    }
    setTimeout(() => {
      this.rtrformgrp.reset();
      this.submitRTR = false;
      this.pendingOfferedData = null;
      this.pendingOfferedCandidate = null;
    }, 1000);
  }

  // ── §8 updatestatus — verbatim port of md §8 ────────────────────────
  // Note: the spec source uses `this.candidateval.firstName/.lastName/
  // .user.email/.email/.contractData.*` — `candidateval` is the per-host
  // candidate Input on candidate-card. In a list view there is no
  // singular `candidateval`; each row's candidate arrives as `value`,
  // so those four reads are rewritten as `value.firstName`,
  // `value.lastName`, `value.user.email`, `value.email`,
  // `value.contractData.*`. Everything else (`this.jobId`,
  // `this.jobTitle`, `this.postedByUserName`) is exposed via getters
  // above so the spec body lands without further edits.
  updatestatus(datas: any, value: any): void {
    this.util.startLoader();
    var data: any = {};
    data.jobId = this.jobId || value?.contractData?.jobId;
    data.candidateId = value.candidateId;
    data.currentStatus = datas.itemId;
    data.candidateName = value.firstName + " " + value.lastName;
    data.jobTitle = this.jobTitle || value?.contractData?.jobTitle || value?.jobTitle;
    data.createdByUserName = this.postedByUserName || '';
    data.createdBy = value.createdBy || '';
    data.email = value?.user?.email || value?.email || '';

    this.jobupdatestatus(data, value);
  }

  // ── §9 jobupdatestatus — verbatim port of md §9 ─────────────────────
  // Note: the spec source uses `this.candidateval.currentStatus = …`
  // and `this.candidateval?.engagementType`. In this list view
  // `candidate` (the optional second param) is the per-row candidate,
  // so the two `this.candidateval` reads are rewritten as `candidate.…`
  // and `candidate?.…`. Behaviour is identical otherwise — same API
  // call, same OFFER-ACCEPTED / ONBOARDED side-effects, same searchData
  // broadcast, same emits, same 99998 / 99996 handling.
  async jobupdatestatus(data: any, candidate?: any): Promise<void> {
    // ----- TEMPORARY free-plan gate (applicant-menu path — this list view is what the Applicants
    // page actually renders). OFFER-ACCEPTED -> candidate owner accepting -> check the CANDIDATE
    // OWNER's plan. CANDIDATE-SELECTED / SELECTED / OFFERED / PRE-ONBOARDED / ONBOARDED -> job owner
    // advancing -> check the acting (logged-in) business's plan. Only code 10004 (free/null plan)
    // blocks; any other code or error proceeds, so Business-plan (paid) users are never blocked. -----
    {
      const planStatus = (data?.currentStatus || '').toString().toUpperCase();
      const ownerPlanStatuses = ['OFFER-ACCEPTED'];
      const actorPlanStatuses = ['CANDIDATE-SELECTED', 'SELECTED', 'OFFERED', 'PRE-ONBOARDED', 'ONBOARDED'];
      if (ownerPlanStatuses.includes(planStatus) || actorPlanStatuses.includes(planStatus)) {
        this.util.startLoader();
        try {
          const planRes: any = ownerPlanStatuses.includes(planStatus)
            ? await this.contractCandidateService.validateBusinessPlan(data.candidateId, '').toPromise()
            : await this.contractCandidateService.validateBusinessPlan('', localStorage.getItem('myBusinessId') || '').toPromise();
          if (planRes?.code === '10004') {
            this.util.stopLoader();
            Swal.fire({
              icon: 'warning',
              title: 'Business Plan Required',
              html: "You can't proceed on the Free plan. Please purchase a Business Plan to continue.",
              confirmButtonText: 'OK'
            });
            return;
          }
        } catch (e) { /* don't hard-block on a plan-check error */ }
        this.util.stopLoader();
      }
    }

    try {
      this.util.startLoader();
      const res: any = await this.apiService.create("candidates/updatejobAppliedStatus", data).toPromise();
      this.util.stopLoader();
      if (res.code === "00000") {
        if (candidate) candidate.currentStatus = data.currentStatus;

        Swal.fire({
          position: "center",
          icon: "success",
          title: "Status successfully Updated.",
          showConfirmButton: false,
          timer: 2000,
        });

        // Non-Corp-to-Corp absorption: freeze other applications at OFFER-ACCEPTED
        const engType = candidate?.engagementType;
        const isNonCorpToCorp = engType && engType !== 'Corp to Corp' && engType !== 'Corp To Corp';

        if (data.currentStatus === 'OFFER-ACCEPTED' && isNonCorpToCorp && data.candidateId) {
          this.contractCandidateService.freezeOtherApplications(data.candidateId, data.jobId).subscribe({
            next: (freezeRes: any) => {
              if (freezeRes.code === '00000' && freezeRes.data?.frozenCount > 0) {
                Swal.fire({
                  icon: 'info',
                  title: 'Applications Frozen',
                  html: `<strong>${freezeRes.data.frozenCount}</strong> other application(s) have been frozen while this offer is pending.`,
                  showConfirmButton: true,
                  confirmButtonText: 'OK'
                });
              }
            },
            error: () => { /* Non-blocking — backend will handle independently */ }
          });
        }

        // Non-Corp-to-Corp absorption: invalidate other applications at ONBOARDED
        if (data.currentStatus === 'ONBOARDED' && isNonCorpToCorp && data.candidateId) {
          this.contractCandidateService.invalidateOtherApplications(data.candidateId, data.jobId).subscribe({
            next: (invRes: any) => {
              if (invRes.code === '00000' && invRes.data?.invalidatedCount > 0) {
                Swal.fire({
                  icon: 'info',
                  title: 'Candidate Absorbed',
                  html: `This candidate has been onboarded. <strong>${invRes.data.invalidatedCount}</strong> other application(s) have been automatically closed and notifications sent.`,
                  showConfirmButton: true,
                  confirmButtonText: 'OK'
                });
              }
            },
            error: () => { /* Non-blocking */ }
          });
        }

        data.eachCandidate = true;
        this.searchData.setJobID(data);
        // activitydate set for api response by monisha
        const appliedCandidate = res.data.jobData.appliedBy.find((c: any) => c.userId === data.candidateId);
        data.activityDate = appliedCandidate ? appliedCandidate.date : null;
        data.isSelected = true;
        this.jobapp_to_newjob_to_atvity.emit(data);
        if (candidate) {
          this.statusUpdated.emit(candidate);
        }

      } else if (res.code == "99998") {
        Swal.fire({
          position: "center",
          icon: "info",
          title: "Job application status updated already..!",
          showConfirmButton: true,
          allowEscapeKey: false,
          allowOutsideClick: false,
        }).then((result) => {
          if (result.isConfirmed) {
            this.JobServicecolor.confirmResult();
          }
        });
      } else if (res.code == '99996') {
        // Mandatory-document gate — shared wording with candidate-card.
        this.contractCandidateService.showMandatoryDocumentPopup();
      }
    } catch (err) {
      this.util.stopLoader();
    }
  }

  openDetails(item:any){
    this.showDetails.emit(item);
  }

  invite(item:any){
    this.inviteClicked.emit(item);
  }

  like(values: any) {
    if (values.isProcessingLike) return;
    if (values.createdBy != localStorage.getItem("userId")) {
      let datas: any = {};
      datas.candidateId = values.candidateId;
      datas.userId = localStorage.getItem("userId");

      const likeStatus = values.isLiked === true ? "UNLIKE" : "LIKE";

      values.isProcessingLike = true;
      this.util.startLoader();
      datas.likeStatus = likeStatus;

      this.apiService.updatePut("candidates/updateCandidateLiked", datas).pipe(takeUntil(this.destroy$)).subscribe((res) => {
        if (res.code == "00000") {
          this.util.stopLoader();
          values.isLiked = res.data.candidateData.isLiked;
          values.likesCount = res.data.candidateData.likesCount;
        }
        values.isProcessingLike = false;
      }, err => {
        values.isProcessingLike = false;
        this.util.stopLoader();
      });
    }else{
      this.goTo(values, 'Clike-like')
    }
  }

  goTo(values: any, param) {
    if (values.createdBy == this.userId) {
      if (param == "jobsApplied") {
        this.router.navigate(['newcandidates'], { queryParams: { candidateId: values.candidateId, tabName: 'jobsApplied', From: this.From } })
      } else if (param == "views") {
        this.router.navigate(['newcandidates'], { queryParams: { candidateId: values.candidateId, tabName: 'views', From: this.From } })

      } else if (param == "Clike-like") {
        this.router.navigate(['newcandidates'], { queryParams: { candidateId: values.candidateId, tabName: 'Clike-like', From: this.From } })

      } else if (param == "cInvited" && values.interestShownCount > 0) {
        this.router.navigate(['newcandidates'], { queryParams: { candidateId: values.candidateId, tabName: 'cInvited', From: this.From } })

      }
    } else if (values.createdBy != this.userId) {
      if (param == "jobsApplied") {
        this.router.navigate(['newcandidates'], { queryParams: { candidateId: values.candidateId, tabName: 'profileSummary', From: this.From } })
      } else if (param == "views" && values.viewedByCount > 0) {
        this.router.navigate(['newcandidates'], { queryParams: { candidateId: values.candidateId, tabName: 'profileSummary', From: this.From } })
      } else if (param == "cInvited" && values.interestShownCount > 0) {
        this.router.navigate(['newcandidates'], { queryParams: { candidateId: values.candidateId, tabName: 'profileSummary', From: this.From } })
      }
    }
  }

  showPercentageDetails: boolean = false;
  details:any;
  openPercentageDetails(item:any){
    this.showPercentageDetails = true;
    // Ensure we pass organisationId from localStorage or jobDetails
    this.details = {
      ...item,
      organisationId: item.organisationId || localStorage.getItem('organisationId') || this.jobDetails?.organisationId
    };
  }

  closePercentageDetails(){
    this.showPercentageDetails= false;
    this.details = null;
  }

  getStageIndex(status: string): number {
    if (!status) return -1;
    // Reject paths aren't on the happy-path pipe; return -1 so no stage
    // is highlighted as "current" rather than falsely marking "Applied"
    // (previous `return idx >= 0 ? idx : 0` showed "Applied — current"
    // for rejected candidates, which misrepresents their state).
    if (this.isRejectedStatus(status)) return -1;
    const stages = ['JOB-APPLIED', 'CANDIDATE-SHORTLISTED', 'INTERVIEW-SCHEDULED', 'CANDIDATE-SELECTED', 'OFFERED', 'ONBOARDED'];
    const idx = stages.findIndex(s => status?.toUpperCase()?.includes(s.split('-')[0]));
    return idx >= 0 ? idx : -1;
  }

  /** Rejection / withdrawal statuses that should not map to any happy-path
   *  stage in the fallback pipeline. Used by `getStageIndex` and the
   *  `.negative` class on the generic stage-pipe fallback. */
  isRejectedStatus(status: string): boolean {
    if (!status) return false;
    const s = status.toUpperCase();
    return s === 'REJECTED'
        || s === 'INTERVIEW-REJECTED'
        || s === 'INTERVIEW-FAILED'
        || s === 'OFFER-DECLINED'
        || s === 'OFFER-WITHDRAWN'
        || s === 'PROFILE-REJECTED';
  }

  // ═════════════════════════════════════════════════════════════════════
  // Submit For (sub-contracting chain push) — append a new SubmittedForEntry
  // when current holder pushes the candidate up to the next upstream business.
  // Backend: POST /sub-contract/submit-for/{jobId}/{applicantUserId}
  // ═════════════════════════════════════════════════════════════════════

  submitForModalRef?: BsModalRef;
  submitForCandidate: any = null;
  submitForUpstreamOptions: any[] = [];
  // Display string for the read-only upstream business input — joined names when
  // there are multiple options. The form control still stores the businessId of
  // the first option (the one submitted to the backend).
  submitForUpstreamName: string = '';
  // businessId → businessName cache. Populated once on init via findConnections
  // so getUpstreamCustomerCandidates can surface real names instead of UUIDs.
  private businessNameMap = new Map<string, string>();
  submitForGroup: UntypedFormGroup = this.formBuilder.group({
    customerBusinessId: ['', Validators.required],
    submittedRate: ['', [Validators.required, CustomValidator.validDecimal(), CustomValidator.checkWhiteSpace()]],
    submittedRateType: ['Hourly', Validators.required],
  });
  submitForSubmitting = false;

  /** Reads the viewer's businessId once for chain/upstream checks. */
  private getViewerBusinessId(): string {
    return localStorage.getItem('myBusinessId')
        || localStorage.getItem('businessId') || '';
  }

  /** L2-INTERVIEW gate — same status set as the backend isStatusAtOrAboveL2. */
  private isStatusAtOrAboveL2(status: string): boolean {
    if (!status) return false;
    const upper = status.toUpperCase().trim();
    const eligible = [
      'L2-INTERVIEW', 'L2_INTERVIEW', 'L2 INTERVIEW',
      'CANDIDATE-SELECTED', 'CANDIDATE_SELECTED', 'SELECTED',
      'OFFERED',
      'OFFER-ACCEPTED', 'OFFER_ACCEPTED',
      'PRE-ONBOARDED', 'PRE_ONBOARDED',
      'ONBOARDED'
    ];
    return eligible.includes(upper);
  }

  /**
   * Visibility predicate for the Submit For icon.
   * Shown when ALL hold:
   *   1. pageName == 'job-applicants' (icon only on the Applicants list)
   *   2. applicant status ≥ L2-INTERVIEW
   *   3. viewer business == latest chain[].customerBusinessId (current holder)
   *   4. at least one upstream business not already in chain[].customerBusinessId
   */
  canSubmitForUpstream(item: any): boolean {
    if (this.pageName !== 'job-applicants') return false;
    if (!this.isStatusAtOrAboveL2(item?.currentStatus)) return false;
    const chain: any[] = item?.submittedForChain || [];
    if (chain.length === 0) return false;
    const viewerBiz = this.getViewerBusinessId().toLowerCase();
    if (!viewerBiz) return false;
    const latestCustomer = (chain[chain.length - 1]?.customerBusinessId || '').toLowerCase();
    if (latestCustomer !== viewerBiz) return false;
    return this.getUpstreamCustomerCandidates(item).length > 0;
  }

  /**
   * Visibility check for the Submit For icon — broader than canSubmitForUpstream.
   * Returns true when EITHER the viewer can currently submit (enabled state)
   * OR the viewer has already pushed this candidate upstream (disabled state).
   * Past-sender visibility lets sub-contractors see "I already submitted this one"
   * without being able to re-click. Top of chain (Ford) never enters either branch
   * since they're never a supplier and have no upstream of their own.
   *
   * Past-sender visibility additionally requires the viewer to have at least one
   * outgoing tier (i.e., a tier row where supplier=viewer). Without this guard,
   * direct-apply candidates on tier-less marketplace / free-plan jobs would show
   * the icon to their candidate-owner — the apply path now seeds entry[0] of the
   * chain with supplier=candidateOwnerBusiness even for direct applies, and
   * without an upstream tier there's nothing they could ever submit-for.
   */
  shouldShowSubmitForIcon(item: any): boolean {
    if (this.pageName !== 'job-applicants') return false;
    if (!this.isStatusAtOrAboveL2(item?.currentStatus)) return false;
    if (this.canSubmitForUpstream(item)) return true;
    const chain: any[] = item?.submittedForChain || [];
    const viewerBiz = this.getViewerBusinessId().toLowerCase();
    if (!viewerBiz) return false;
    const job = this.appliedJobDetails || item?.jobDetails;
    const tiers: any[] = job?.tierPayRates || [];
    const hasOutgoingTier = tiers.some(
      (t: any) => (t?.supplierBusinessId || '').toLowerCase() === viewerBiz
    );
    if (!hasOutgoingTier) return false;
    return chain.some(e => (e?.supplierBusinessId || '').toLowerCase() === viewerBiz);
  }

  /**
   * Returns list of upstream businesses the viewer can submit TO.
   * Source: tierPayRates rows where supplier == viewer. Excludes any business
   * already present in chain[].customerBusinessId. Business names are resolved
   * via businessNameMap (populated on init from findConnections); when the name
   * isn't cached, businessName is left empty (display logic decides fallback).
   */
  getUpstreamCustomerCandidates(item: any): any[] {
    const job = this.appliedJobDetails || item?.jobDetails;
    if (!job) return [];
    const viewerBiz = this.getViewerBusinessId().toLowerCase();
    const chain: any[] = item?.submittedForChain || [];
    const seen = new Set<string>(
      chain.map(e => (e?.customerBusinessId || '').toLowerCase()).filter(Boolean)
    );

    const out: any[] = [];
    const pushUnique = (bizId: string, name: string) => {
      const key = (bizId || '').toLowerCase();
      if (!key || key === viewerBiz || seen.has(key)) return;
      if (out.find(o => (o.businessId || '').toLowerCase() === key)) return;
      const resolvedName = name || this.businessNameMap.get(key) || '';
      out.push({ businessId: bizId, businessName: resolvedName });
    };

    const tiers: any[] = job?.tierPayRates || [];
    for (const t of tiers) {
      if (!t) continue;
      if ((t.supplierBusinessId || '').toLowerCase() === viewerBiz) {
        pushUnique(t.customerBusinessId, t.customerBusinessName);
      }
    }
    return out;
  }

  /** Opens the Submit For modal pre-filled with upstream options + default rate. */
  openSubmitForModal(item: any, template: TemplateRef<any>): void {
    this.submitForCandidate = item;
    this.submitForUpstreamOptions = this.getUpstreamCustomerCandidates(item);
    // Display: business names only — joined with " / " when more than one upstream
    // resolves. Falls back to "Upstream business" when no name is cached so the
    // field never shows raw UUIDs.
    const names = this.submitForUpstreamOptions
        .map(u => (u?.businessName || '').trim())
        .filter(Boolean);
    this.submitForUpstreamName = names.length > 0
        ? names.join(' / ')
        : (this.submitForUpstreamOptions.length > 0 ? 'Upstream business' : '');
    this.submitForSubmitting = false;

    // Default rate hint = latest entry's rate (so revising suppliers default to their last submitted rate)
    const chain: any[] = item?.submittedForChain || [];
    const latest = chain[chain.length - 1];
    this.submitForGroup.reset({
      customerBusinessId: this.submitForUpstreamOptions[0]?.businessId || '',
      submittedRate: latest?.submittedRate || item?.targetRate || '',
      submittedRateType: latest?.submittedRateType || item?.payType || 'Hourly',
    });

    const opts: ModalOptions = { class: 'modal-md', backdrop: 'static', keyboard: false };
    this.submitForModalRef = this.modalService.show(template, opts);
  }

  closeSubmitForModal(): void {
    this.submitForModalRef?.hide();
    this.submitForModalRef = undefined;
    this.submitForCandidate = null;
  }

  confirmSubmitForUpstream(): void {
    if (this.submitForGroup.invalid || !this.submitForCandidate) {
      this.submitForGroup.markAllAsTouched();
      return;
    }
    const jobId = this.jobId || this.submitForCandidate?.jobDetails?.jobId;
    const applicantUserId = this.submitForCandidate?.candidateId || this.submitForCandidate?.userId;
    if (!jobId || !applicantUserId) {
      Swal.fire({ icon: 'error', title: 'Missing context', text: 'Job or applicant id is unavailable.' });
      return;
    }

    const payload: SubmittedForPayload = {
      customerBusinessId: this.submitForGroup.value.customerBusinessId,
      supplierBusinessId: this.getViewerBusinessId(),
      submittedByUserId: this.userId || localStorage.getItem('userId') || '',
      submittedRate: String(this.submitForGroup.value.submittedRate || ''),
      submittedRateType: this.submitForGroup.value.submittedRateType,
    };

    this.submitForSubmitting = true;
    this.subContractService.submitForUpstream(jobId, applicantUserId, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.submitForSubmitting = false;
          // Backend success code is "00000" (5-zero string) per the rest of the codebase
          // — agreement-document-checklist.service.ts:431, contract-candidate.service.ts:272, etc.
          if (res?.code === '00000') {
            // Sync the local row so the chain chip + icon visibility update without a full reload.
            this.submitForCandidate.submittedForChain = res?.data?.submittedForChain
                || [...(this.submitForCandidate.submittedForChain || []), {
                  customerBusinessId: payload.customerBusinessId,
                  supplierBusinessId: payload.supplierBusinessId,
                  submittedByUserId: payload.submittedByUserId,
                  submittedOn: new Date().toISOString(),
                  submittedRate: payload.submittedRate,
                  submittedRateType: payload.submittedRateType,
                }];
            this.closeSubmitForModal();
            Swal.fire({ icon: 'success', title: 'Submitted', text: 'Candidate submitted to upstream business.', timer: 1800, showConfirmButton: false });
          } else {
            Swal.fire({ icon: 'error', title: 'Submit failed', text: res?.message || 'Unable to submit for upstream.' });
          }
        },
        error: (err) => {
          this.submitForSubmitting = false;
          Swal.fire({ icon: 'error', title: 'Submit failed', text: err?.error?.message || err?.message || 'Network error.' });
        }
      });
  }

  /** Latest chain entry for chip rendering. Returns null if no chain. */
  getLatestChainEntry(item: any): any {
    const chain: any[] = item?.submittedForChain || [];
    return chain.length > 0 ? chain[chain.length - 1] : null;
  }

}
